// Google Analytics 4 integration
declare global {
  interface Window {
    gtag: (...args: any[]) => void
    dataLayer: any[]
  }
}

export const GA_TRACKING_ID = import.meta.env.VITE_GA_TRACKING_ID

// Initialize Google Analytics
export const initGA = () => {
  if (!GA_TRACKING_ID) {
    // Only show warning in production
    if (import.meta.env.PROD) {
      console.warn('Google Analytics tracking ID not found')
    }
    return
  }

  // Create script tag for gtag
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`
  document.head.appendChild(script)

  // Initialize dataLayer and gtag
  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() {
    window.dataLayer.push(arguments)
  }

  window.gtag('js', new Date())
  window.gtag('config', GA_TRACKING_ID, {
    page_title: document.title,
    page_location: window.location.href,
  })
}

// Track page views
export const trackPageView = (url: string, title?: string) => {
  if (!window.gtag) return

  window.gtag('config', GA_TRACKING_ID, {
    page_path: url,
    page_title: title || document.title,
  })
}

// Track events
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
) => {
  if (!window.gtag) return

  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value,
  })
}

// Track custom events
export const trackCustomEvent = (eventName: string, parameters: Record<string, any>) => {
  if (!window.gtag) return

  window.gtag('event', eventName, parameters)
}

// Predefined tracking functions
export const analytics = {
  // Core tracking method
  trackEvent: (action: string, category: string, label?: string, value?: number) => {
    trackEvent(action, category, label, value)
  },

  // Navigation tracking
  trackNavigation: (section: string) => {
    trackEvent('navigate', 'Navigation', section)
  },

  // Contact form tracking
  trackContactForm: (action: 'start' | 'submit' | 'success' | 'error', details?: string) => {
    trackEvent(action, 'Contact Form', details)
  },

  // Download tracking
  trackDownload: (fileName: string, fileType: string) => {
    trackEvent('download', 'File Download', `${fileName}.${fileType}`)
  },

  // Social media tracking
  trackSocialClick: (platform: string, action: string) => {
    trackEvent('click', 'Social Media', `${platform}_${action}`)
  },

  // Project interaction tracking
  trackProjectView: (projectName: string) => {
    trackEvent('view', 'Project', projectName)
  },

  trackProjectLink: (projectName: string, linkType: 'demo' | 'github') => {
    trackEvent('click', 'Project Link', `${projectName}_${linkType}`)
  },

  // Theme tracking
  trackThemeChange: (theme: 'light' | 'dark') => {
    trackEvent('change', 'Theme', theme)
  },

  // Performance tracking
  trackPerformance: (metric: string, value: number, rating: string) => {
    trackCustomEvent('web_vitals', {
      metric_name: metric,
      metric_value: value,
      metric_rating: rating,
    })
  },

  // Error tracking
  trackError: (error: string, component?: string) => {
    trackEvent('error', 'JavaScript Error', `${component || 'Unknown'}: ${error}`)
  },

  // Engagement tracking
  trackEngagement: (action: string, element: string, duration?: number) => {
    trackCustomEvent('user_engagement', {
      engagement_action: action,
      engagement_element: element,
      engagement_duration: duration,
    })
  },

  // Scroll tracking
  trackScroll: (percentage: number) => {
    if (percentage === 25 || percentage === 50 || percentage === 75 || percentage === 100) {
      trackEvent('scroll', 'Page Scroll', `${percentage}%`)
    }
  },

  // Time on page tracking
  trackTimeOnPage: (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    if (minutes > 0 && minutes % 1 === 0) {
      trackEvent('timing', 'Time on Page', `${minutes} minutes`)
    }
  },

  // Custom event tracking
  trackCustomEvent: (eventName: string, parameters: Record<string, any>) => {
    trackCustomEvent(eventName, parameters)
  },
}

// Scroll tracking hook
export const useScrollTracking = () => {
  let ticking = false

  const handleScroll = () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop
        const docHeight = document.documentElement.scrollHeight - window.innerHeight
        const scrollPercent = Math.round((scrollTop / docHeight) * 100)
        
        analytics.trackScroll(scrollPercent)
        ticking = false
      })
      ticking = true
    }
  }

  return handleScroll
}

// Time tracking hook
export const useTimeTracking = () => {
  let startTime = Date.now()

  const reportTime = () => {
    const currentTime = Date.now()
    const totalSeconds = Math.floor((currentTime - startTime) / 1000)
    analytics.trackTimeOnPage(totalSeconds)
  }

  // Report time every minute
  const interval = setInterval(reportTime, 60000)

  return () => {
    clearInterval(interval)
    reportTime() // Final report
  }
}

// Enhanced error boundary tracking
export const trackErrorBoundary = (error: Error, errorInfo: any) => {
  analytics.trackError(error.message, errorInfo.componentStack)
  
  // Send detailed error to analytics
  trackCustomEvent('error_boundary', {
    error_message: error.message,
    error_stack: error.stack,
    component_stack: errorInfo.componentStack,
    timestamp: new Date().toISOString(),
  })
}